/**
 * Auto Quote Infrastructure - API Adapter
 *
 * Wraps Orval-generated quote response hooks with type-safe BaseResponseDto handling.
 * Used for one-click quote submission from Kakao alimtalk buttons.
 */

import type { BaseResponseDto } from "@semo/api-contracts";
import { useQuoteResponsesControllerCreate } from "@semo/api-contracts";
import type { QuoteResponseFlowResultDto } from "@semo/api-contracts";

/**
 * Unwrap BaseResponseDto to get actual data
 */
function unwrapResponse<T>(response: unknown): T | undefined {
  return (response as BaseResponseDto & { data?: T })?.data;
}

/**
 * Auto-quote submission result (one-click from Kakao alimtalk)
 */
export interface AutoQuoteSubmitResult {
  quoteResponseId: string;
  versionNumber: number;
  chatRoomId: string;
  status: "submitted" | "processing";
  createdAt: string;
}

/**
 * Type-safe wrapper for auto-quote submission
 *
 * Wraps the existing quote response creation endpoint with:
 * - BaseResponseDto unwrapping
 * - Type-safe result extraction
 * - Auto-submission specific naming
 */
export function useSubmitAutoQuote(
  options?: Parameters<typeof useQuoteResponsesControllerCreate>[0],
) {
  const mutation = useQuoteResponsesControllerCreate(options);

  return {
    ...mutation,
    submitAutoQuote: mutation.mutate,
    submitAutoQuoteAsync: mutation.mutateAsync,
    // Helper to unwrap response
    unwrapResult: (response: unknown): AutoQuoteSubmitResult | undefined => {
      return unwrapResponse<QuoteResponseFlowResultDto>(response) as
        | AutoQuoteSubmitResult
        | undefined;
    },
  };
}

/**
 * Export raw Orval hook for special cases
 */
export { useQuoteResponsesControllerCreate as useSubmitAutoQuoteRaw };
